/**
 * Currency context — the single source of truth for the ACTIVE checkout currency.
 *
 * Default is always USD. Two ways a non-USD currency becomes active:
 *
 *  1. Geo (production): `setCurrentCurrencyFromGeo(window.USER_COUNTRY)` maps the visitor's
 *     country to a `mode: 'geo'` currency from the registry (EUR/GBP/CHF/AUD/CAD/NZD), else
 *     USD. Never selects a test currency.
 *
 *  2. md5 URL unlock (TEST currencies only): `setCurrentCurrency(params)` activates a
 *     `mode: 'test'` currency (VUV — a cheap real-money test currency) when BOTH params are
 *     present and hash to the stored unlock:  ?currency=vuv&replace_currency=crepus
 *     The plaintext unlock word never appears in the bundle — only its md5. The unlock can
 *     ONLY select test currencies, so the public geo currencies have no URL force-override.
 *
 * Selection order at paywall init: geo first, then the md5 unlock — which overrides geo when a
 * valid TEST unlock is present and is otherwise a no-op (it never resets the geo currency).
 *
 * See www/src/config/currencies.ts for the registry (multipliers, modes, country lists).
 */

import {
  type CurrencyCode,
  DEFAULT_CURRENCY,
  currencyForCountry,
  getCurrencyDef,
} from '../config/currencies';

export type Currency = CurrencyCode;

// md5('vuv' + 'crepus'). The unlock word is intentionally NOT stored in plaintext.
const UNLOCK_HASH = '53e4fa342ba4749e0823ef8de9eb8924';

let currentCurrency: Currency = DEFAULT_CURRENCY;

/** Returns the active currency. Defaults to USD until geo or the unlock changes it. */
export function getCurrentCurrency(): Currency {
  return currentCurrency;
}

/**
 * Resolve and store the active currency from geo (the visitor's 2-letter country code).
 * Maps to a `mode: 'geo'` currency from the registry, or USD for unknown countries. Never
 * selects a test currency. Safe to call once at paywall init, before pricing is loaded.
 */
export function setCurrentCurrencyFromGeo(country: string | undefined | null): Currency {
  currentCurrency = currencyForCountry(country);
  return currentCurrency;
}

/**
 * Apply the md5 URL unlock for TEST currencies (VUV). When `currency` + `replace_currency`
 * hash to the stored unlock AND name a `mode: 'test'` currency, that currency becomes active.
 * Otherwise this is a NO-OP — it never resets a geo-selected currency. Idempotent.
 */
export function setCurrentCurrency(params: URLSearchParams): Currency {
  const currencyParam = (params.get('currency') || '').trim().toLowerCase();
  const replaceParam = (params.get('replace_currency') || '').trim().toLowerCase();

  if (currencyParam && md5(currencyParam + replaceParam) === UNLOCK_HASH) {
    const candidate = currencyParam.toUpperCase();
    const def = getCurrencyDef(candidate);
    if (def && def.mode === 'test') {
      currentCurrency = candidate as Currency;
    }
  }

  return currentCurrency;
}

/** Test-only reset hook. */
export function __resetCurrencyForTest(): void {
  currentCurrency = DEFAULT_CURRENCY;
}

/* ------------------------------------------------------------------ *
 * Synchronous MD5 (blueimp-md5 core, public domain). Browsers expose
 * only async SHA via crypto.subtle, so a tiny sync md5 is bundled here
 * to keep the one-time unlock check simple. Used solely for the unlock
 * gate above — not for any security-sensitive purpose.
 * ------------------------------------------------------------------ */

function safeAdd(x: number, y: number): number {
  const lsw = (x & 0xffff) + (y & 0xffff);
  const msw = (x >> 16) + (y >> 16) + (lsw >> 16);
  return (msw << 16) | (lsw & 0xffff);
}

function bitRol(num: number, cnt: number): number {
  return (num << cnt) | (num >>> (32 - cnt));
}

function md5cmn(q: number, a: number, b: number, x: number, s: number, t: number): number {
  return safeAdd(bitRol(safeAdd(safeAdd(a, q), safeAdd(x, t)), s), b);
}
function md5ff(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
  return md5cmn((b & c) | (~b & d), a, b, x, s, t);
}
function md5gg(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
  return md5cmn((b & d) | (c & ~d), a, b, x, s, t);
}
function md5hh(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
  return md5cmn(b ^ c ^ d, a, b, x, s, t);
}
function md5ii(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
  return md5cmn(c ^ (b | ~d), a, b, x, s, t);
}

function binlMD5(x: number[], len: number): number[] {
  x[len >> 5] = (x[len >> 5] || 0) | (0x80 << (len % 32));
  x[(((len + 64) >>> 9) << 4) + 14] = len;
  // Fill any holes so reads coerce cleanly (matches JS undefined→0 bitwise coercion).
  for (let k = 0; k <= (((len + 64) >>> 9) << 4) + 15; k++) {
    if (typeof x[k] !== 'number') x[k] = 0;
  }

  let a = 1732584193;
  let b = -271733879;
  let c = -1732584194;
  let d = 271733878;

  for (let i = 0; i < x.length; i += 16) {
    const olda = a;
    const oldb = b;
    const oldc = c;
    const oldd = d;

    a = md5ff(a, b, c, d, x[i], 7, -680876936);
    d = md5ff(d, a, b, c, x[i + 1], 12, -389564586);
    c = md5ff(c, d, a, b, x[i + 2], 17, 606105819);
    b = md5ff(b, c, d, a, x[i + 3], 22, -1044525330);
    a = md5ff(a, b, c, d, x[i + 4], 7, -176418897);
    d = md5ff(d, a, b, c, x[i + 5], 12, 1200080426);
    c = md5ff(c, d, a, b, x[i + 6], 17, -1473231341);
    b = md5ff(b, c, d, a, x[i + 7], 22, -45705983);
    a = md5ff(a, b, c, d, x[i + 8], 7, 1770035416);
    d = md5ff(d, a, b, c, x[i + 9], 12, -1958414417);
    c = md5ff(c, d, a, b, x[i + 10], 17, -42063);
    b = md5ff(b, c, d, a, x[i + 11], 22, -1990404162);
    a = md5ff(a, b, c, d, x[i + 12], 7, 1804603682);
    d = md5ff(d, a, b, c, x[i + 13], 12, -40341101);
    c = md5ff(c, d, a, b, x[i + 14], 17, -1502002290);
    b = md5ff(b, c, d, a, x[i + 15], 22, 1236535329);

    a = md5gg(a, b, c, d, x[i + 1], 5, -165796510);
    d = md5gg(d, a, b, c, x[i + 6], 9, -1069501632);
    c = md5gg(c, d, a, b, x[i + 11], 14, 643717713);
    b = md5gg(b, c, d, a, x[i], 20, -373897302);
    a = md5gg(a, b, c, d, x[i + 5], 5, -701558691);
    d = md5gg(d, a, b, c, x[i + 10], 9, 38016083);
    c = md5gg(c, d, a, b, x[i + 15], 14, -660478335);
    b = md5gg(b, c, d, a, x[i + 4], 20, -405537848);
    a = md5gg(a, b, c, d, x[i + 9], 5, 568446438);
    d = md5gg(d, a, b, c, x[i + 14], 9, -1019803690);
    c = md5gg(c, d, a, b, x[i + 3], 14, -187363961);
    b = md5gg(b, c, d, a, x[i + 8], 20, 1163531501);
    a = md5gg(a, b, c, d, x[i + 13], 5, -1444681467);
    d = md5gg(d, a, b, c, x[i + 2], 9, -51403784);
    c = md5gg(c, d, a, b, x[i + 7], 14, 1735328473);
    b = md5gg(b, c, d, a, x[i + 12], 20, -1926607734);

    a = md5hh(a, b, c, d, x[i + 5], 4, -378558);
    d = md5hh(d, a, b, c, x[i + 8], 11, -2022574463);
    c = md5hh(c, d, a, b, x[i + 11], 16, 1839030562);
    b = md5hh(b, c, d, a, x[i + 14], 23, -35309556);
    a = md5hh(a, b, c, d, x[i + 1], 4, -1530992060);
    d = md5hh(d, a, b, c, x[i + 4], 11, 1272893353);
    c = md5hh(c, d, a, b, x[i + 7], 16, -155497632);
    b = md5hh(b, c, d, a, x[i + 10], 23, -1094730640);
    a = md5hh(a, b, c, d, x[i + 13], 4, 681279174);
    d = md5hh(d, a, b, c, x[i], 11, -358537222);
    c = md5hh(c, d, a, b, x[i + 3], 16, -722521979);
    b = md5hh(b, c, d, a, x[i + 6], 23, 76029189);
    a = md5hh(a, b, c, d, x[i + 9], 4, -640364487);
    d = md5hh(d, a, b, c, x[i + 12], 11, -421815835);
    c = md5hh(c, d, a, b, x[i + 15], 16, 530742520);
    b = md5hh(b, c, d, a, x[i + 2], 23, -995338651);

    a = md5ii(a, b, c, d, x[i], 6, -198630844);
    d = md5ii(d, a, b, c, x[i + 7], 10, 1126891415);
    c = md5ii(c, d, a, b, x[i + 14], 15, -1416354905);
    b = md5ii(b, c, d, a, x[i + 5], 21, -57434055);
    a = md5ii(a, b, c, d, x[i + 12], 6, 1700485571);
    d = md5ii(d, a, b, c, x[i + 3], 10, -1894986606);
    c = md5ii(c, d, a, b, x[i + 10], 15, -1051523);
    b = md5ii(b, c, d, a, x[i + 1], 21, -2054922799);
    a = md5ii(a, b, c, d, x[i + 8], 6, 1873313359);
    d = md5ii(d, a, b, c, x[i + 15], 10, -30611744);
    c = md5ii(c, d, a, b, x[i + 6], 15, -1560198380);
    b = md5ii(b, c, d, a, x[i + 13], 21, 1309151649);
    a = md5ii(a, b, c, d, x[i + 4], 6, -145523070);
    d = md5ii(d, a, b, c, x[i + 11], 10, -1120210379);
    c = md5ii(c, d, a, b, x[i + 2], 15, 718787259);
    b = md5ii(b, c, d, a, x[i + 9], 21, -343485551);

    a = safeAdd(a, olda);
    b = safeAdd(b, oldb);
    c = safeAdd(c, oldc);
    d = safeAdd(d, oldd);
  }
  return [a, b, c, d];
}

function binl2rstr(input: number[]): string {
  let output = '';
  const length32 = input.length * 32;
  for (let i = 0; i < length32; i += 8) {
    output += String.fromCharCode((input[i >> 5] >>> (i % 32)) & 0xff);
  }
  return output;
}

function rstr2binl(input: string): number[] {
  const output: number[] = [];
  const length8 = input.length * 8;
  for (let i = 0; i < length8; i += 8) {
    const idx = i >> 5;
    output[idx] = (output[idx] || 0) | ((input.charCodeAt(i / 8) & 0xff) << (i % 32));
  }
  return output;
}

function rstr2hex(input: string): string {
  const hexTab = '0123456789abcdef';
  let output = '';
  for (let i = 0; i < input.length; i++) {
    const x = input.charCodeAt(i);
    output += hexTab.charAt((x >>> 4) & 0x0f) + hexTab.charAt(x & 0x0f);
  }
  return output;
}

function md5(str: string): string {
  const utf8 = unescape(encodeURIComponent(str)); // handle non-ASCII safely
  return rstr2hex(binl2rstr(binlMD5(rstr2binl(utf8), utf8.length * 8)));
}
